import moment from 'moment';
import { HISTORY_ACTIONS, HISTORY_OBJECT_TYPES } from './constant';

/**
 * Constrains the HSL color values to a specific range.
 */
function constrainToColorRange([h, s, l]: number[]): string {
  // constrain saturation value to one that falls within range of colors that isn't blindingly bright
  const newS = Math.round(70 * (s / 100));

  // constrain luminence value to one that falls within range of colors light enough to be backgrounds for black text but dark enough to stand out from the light grey dashboard background
  const newL = Math.round(76 + 9 * (l / 100));

  // constrain hue value to one that falls within blue/green range of colors
  const newH = Math.round(206 + 66 * (h / 358));

  const colorInHSL = `hsl(${newH}, ${newS}%, ${newL}%)`;
  return colorInHSL;
}

/**
 * given a hex value as a string that represents a color, returns an hsl representation of the same color
 */
function toHSL(hex: string): number[] {
  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  const [r, g, b] = match.slice(1).map((v) => parseInt(v, 16) / 255);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  s = Math.round(s * 100);
  l = Math.round(l * 100);
  h = Math.round(360 * h);

  return [h, s, l];
}

/**
 * given an integer, returns a hex value corresponding to a css color that falls within the web-safe color palette
 */
function intToRGB(i: number): string {
  // get hex value
  const h = i & 0x00ffffff;

  // format as hex string
  let str = h.toString(16).toUpperCase();
  str = '00000'.substring(0, 6 - str.length) + str;

  return str;
}

// given a string, returns an integer code corresponding to that string
// (used in generating colors for items based on their types)
function hashCode(str: string): number {
  // java String#hashCode
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

/**
 * given a string, returns a string hsl representation of a color based on a hardwired mapping function
 */
export function generateColorFromString(str: string): string {
  switch (str.toLowerCase()) {
    case 'project':
      return 'rgb(150,185,220)';
    case 'task':
      return 'rgb(191,186,207)';
    case 'bug':
      return 'rgb(234,153,153)';
    case '':
      return 'rgb(168,179,219)';
    default:
      return constrainToColorRange(toHSL(intToRGB(hashCode(str))));
  }
}

/**
 * given a string email, return just the prefix of that email as a string e.g., given 'dana.bullister@gmail.com', returns 'dana.bullister'
 */
export function getPrefix(email: string): string {
  return email.substring(0, email.indexOf('@'));
}

/**
 * given a string with a comma separated list of concatenated email addresses, return the same string but without the suffixes of the emails e.g.,"example@abc.com, example2@abc.com" -> "example1, example2"
 */
function removeAllSuffixes(emails: string): string {
  if (emails.indexOf('@') == -1) {
    return emails;
  } else {
    const ind1 = emails.indexOf('@');
    const ind2 = emails.indexOf(',', ind1);
    let minusSuffix;
    if (ind2 == -1) {
      minusSuffix = emails.substring(0, ind1);
    } else {
      minusSuffix = emails.substring(0, ind1).concat(emails.substring(ind2));
    }
    return removeAllSuffixes(minusSuffix);
  }
}

function formatTimestamp(timestamp) {
  const date = moment(timestamp);

  // Check if the date is today
  if (date.isSame(moment(), 'day')) {
    // Format the output for today
    return `Today at ${date.format('h:mm A')}`;
  } else {
    // Format for other dates as "MMM D at h:mm A"
    return `${date.format('MMM D')} at ${date.format('h:mm A')}`;
  }
}

/**
 * given an item history record object, returns a string of text to be displayed to the user communicating the contents of that record
 */
export function generateHistoryRecordText(record: any): string {
  const reformattedTimestamp = formatTimestamp(record.timestamp);
  const userDisplayText = getPrefix(record.user);
  const formattedName = removeAllSuffixes(record.object_name);

  // Determine the appropriate entry text based on record type and action
  const entry = getHistoryEntryText(record, userDisplayText, formattedName);

  return [reformattedTimestamp, entry.join('')].join('::');
}

/**
 * Generate the appropriate history entry text based on the record type and action
 */
function getHistoryEntryText(record: any, userDisplayText: string, formattedName: string): string[] {
  const { object_type, action, was_automated_action, before_value, after_value } = record;

  // Item-related records
  if (object_type === HISTORY_OBJECT_TYPES.ITEM && (action === HISTORY_ACTIONS.ADD || action === HISTORY_ACTIONS.REMOVE || action === HISTORY_ACTIONS.UNDELETE)) {
    return getItemHistoryText(action, was_automated_action, userDisplayText);
  }

  // Share permission records
  if (object_type === HISTORY_OBJECT_TYPES.SHARE_PERMISSION && (action === HISTORY_ACTIONS.ADD || action === HISTORY_ACTIONS.REMOVE)) {
    return getSharePermissionHistoryText(action, was_automated_action, userDisplayText, formattedName);
  }

  // Attachment records
  if ((object_type === HISTORY_OBJECT_TYPES.ATTACHMENT || object_type === HISTORY_OBJECT_TYPES.ATTACHMENTS) && (action === HISTORY_ACTIONS.ADD || action === HISTORY_ACTIONS.REMOVE)) {
    return getAttachmentHistoryText(object_type, action, userDisplayText, formattedName);
  }

  // Assignee records
  if (object_type === HISTORY_OBJECT_TYPES.ASSIGNEE && (action === HISTORY_ACTIONS.ADD || action === HISTORY_ACTIONS.REMOVE)) {
    return getAssigneeHistoryText(action, was_automated_action, userDisplayText, formattedName);
  }

  // Subscriber records
  if (object_type === HISTORY_OBJECT_TYPES.SUBSCRIBER && (action === HISTORY_ACTIONS.ADD || action === HISTORY_ACTIONS.REMOVE)) {
    return getSubscriberHistoryText(action, was_automated_action, userDisplayText, formattedName);
  }

  // Default case for property updates
  return getPropertyUpdateHistoryText(object_type, was_automated_action, userDisplayText, before_value, after_value);
}

/**
 * Generate history text for item actions
 */
function getItemHistoryText(action: string, wasAutomated: boolean, userDisplayText: string): string[] {
  if (action === HISTORY_ACTIONS.ADD) {
    return wasAutomated ? ['Item autocreated'] : ['Item created by ', userDisplayText];
  }

  if (action === HISTORY_ACTIONS.REMOVE) {
    return wasAutomated ? ['Item automoved to trash'] : ['Item moved to trash by ', userDisplayText];
  }

  if (action === HISTORY_ACTIONS.UNDELETE) {
    return wasAutomated ? ['Item automoved from trash'] : ['Item moved from trash by ', userDisplayText];
  }
}

/**
 * Generate history text for share permission actions
 */
function getSharePermissionHistoryText(action: string, wasAutomated: boolean, userDisplayText: string, formattedName: string): string[] {
  if (action === HISTORY_ACTIONS.ADD) {
    return wasAutomated
      ? ['Item shared with ', formattedName]
      : [userDisplayText, ' shared item with ', formattedName];
  }

  if (action === HISTORY_ACTIONS.REMOVE) {
    return wasAutomated
      ? ['Share permission removed for ', formattedName]
      : [userDisplayText, ' stopped sharing item with ', formattedName];
  }
}

/**
 * Generate history text for attachment actions
 */
function getAttachmentHistoryText(objectType: string, action: string, userDisplayText: string, formattedName: string): string[] {
  const isSingular = objectType === HISTORY_OBJECT_TYPES.ATTACHMENT;
  const suffix = isSingular ? ' as an attachment' : ' as attachments';

  if (action === HISTORY_ACTIONS.ADD) {
    return [userDisplayText, ' added ', formattedName, suffix];
  }

  if (action === HISTORY_ACTIONS.REMOVE) {
    return [userDisplayText, ' removed ', formattedName, suffix];
  }
}

/**
 * Generate history text for assignee actions
 */
function getAssigneeHistoryText(action: string, wasAutomated: boolean, userDisplayText: string, formattedName: string): string[] {
  if (action === HISTORY_ACTIONS.ADD) {
    return wasAutomated
      ? [formattedName, ' added as an assignee']
      : [userDisplayText, ' added ', formattedName, ' as an assignee'];
  }

  if (action === HISTORY_ACTIONS.REMOVE) {
    return wasAutomated
      ? [formattedName, ' removed as an assignee']
      : [userDisplayText, ' removed ', formattedName, ' as an assignee'];
  }
}

/**
 * Generate history text for subscriber actions
 */
function getSubscriberHistoryText(action: string, wasAutomated: boolean, userDisplayText: string, formattedName: string): string[] {
  if (action === HISTORY_ACTIONS.ADD) {
    return wasAutomated
      ? [formattedName, ' added as a subscriber']
      : [userDisplayText, ' added ', formattedName, ' as a subscriber'];
  }

  if (action === HISTORY_ACTIONS.REMOVE) {
    return wasAutomated
      ? [formattedName, ' removed as a subscriber']
      : [userDisplayText, ' removed ', formattedName, ' as a subscriber'];
  }
}

/**
 * Generate history text for property updates
 */
function getPropertyUpdateHistoryText(objectType: string, wasAutomated: boolean, userDisplayText: string,
  beforeValue: string, afterValue: string): string[] {
  if (objectType === HISTORY_OBJECT_TYPES.TAGS) {
    return wasAutomated
      ? [objectType, ' updated from ', beforeValue, ' to ', afterValue, '']
      : [userDisplayText, ' updated ', objectType, ' from ', beforeValue, ' to ', afterValue, ''];
  }

  return wasAutomated
    ? [objectType, ' updated from "', beforeValue, '" to "', afterValue, '"']
    : [userDisplayText, ' updated ', objectType, ' from "', beforeValue, '" to "', afterValue, '"'];
}
